import { useSelector } from 'react-redux';
import type { RootState } from '../types';
import { useMemo } from 'react';

const getPropertiesFromObject = (obj: object) => Object.keys(obj).map(Number);
const excludeNonExistentIDs = (arr1: any[], arr2: string | any[]) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return arr1.filter(id => arr2.includes(id));
};

interface Value<T> {
    value: T | undefined;
    index: number;
}

const toValue = <T>(arr: T[], index: number): Value<T> => ({ value: arr[index], index });

const getClosest = <T>(val1: T, val2: T, target: number, cb: (data: T) => number): T => {
    if (target - cb(val1) >= cb(val2) - target) {
        return val2;
    }

    return val1;
};

const uniqueNeighbors = 1;

export const findClosest = <T>(arr: T[], target: number, cb: (data: T | undefined) => number): Value<T> => {
    const n = arr.length;

    // Corner cases
    if (target <= cb(arr[0])) {
        return toValue(arr, 0);
    }
    if (target >= cb(arr[n - 1])) {
        return toValue(arr, n - 1);
    }

    // Binary search
    let i = 0;
    let j = n;
    let mid = 0;
    while (i < j) {
        mid = Math.floor((i + j) / 2);

        if (cb(arr[mid]) == target) {
            return toValue(arr, mid);
        }

        if (target < cb(arr[mid])) {
            if (mid > 0 && target > cb(arr[mid - 1])) {
                return getClosest(toValue(arr, mid - 1), toValue(arr, mid), target, x => cb(x.value));
            }

            j = mid;
        } else {
            if (mid < n - 1 && target < cb(arr[mid + 1])) {
                return getClosest(toValue(arr, mid), toValue(arr, mid + 1), target, x => cb(x.value));
            }

            i = mid + 1;
        }
    }

    return toValue(arr, mid);
};

export const findClosestIndex = <T>(
    arr: T[],
    target: number,
    cb: (data: T | undefined) => number,
): Value<T>['index'] => {
    const { index } = findClosest(arr, target, cb);

    return index;
};

export const findClosestValue = <T>(
    arr: T[],
    target: number,
    cb: (data: T | undefined) => number,
): Value<T>['value'] => {
    const { value } = findClosest(arr, target, cb);

    return value;
};

export const slice = <T>(data: T[], idx: number, size = 2): T[] => {
    const startIdx = Math.max(idx - size, 0);
    const endIdx = idx + size;

    return data.slice(startIdx, endIdx);
};

export const interpolateLagrangePolynomial =
    (xValues: number[], yValues: number[]) =>
    (x: number): number => {
        const size = Math.min(xValues.length, yValues.length);
        let lagrangePol = 0;

        for (let i = 0; i < size; i++) {
            let basicsPol = 1;

            for (let j = 0; j < size; j++) {
                if (j != i) {
                    basicsPol *= (x - (xValues[j] ?? 0)) / ((xValues[i] ?? 0) - (xValues[j] ?? 0));
                }
            }

            lagrangePol += basicsPol * (yValues[i] ?? 0);
        }

        return lagrangePol;
    };

export const interpolateLagrange = (
    xValues: number[],
    yValues: number[],
    from: number,
    to: number,
): Array<[number, number]> => {
    const result: Array<[number, number]> = [];
    
    for (let i = from; i <= to; i++) {
        const { index } = findClosest(xValues, i, x => x ?? 0);

        const xValueSliced = slice(xValues, index);
        const yValueSliced = slice(yValues, index);

        const interpolate = interpolateLagrangePolynomial(xValueSliced, yValueSliced);
        // if (!Number.isFinite(interpolate(i))) {
        //     console.log(i, xValueSliced, yValueSliced);
        // }

        result.push([i, interpolate(i)]);
    }

    return result;
};

export const getFromLimits = (from: number, to: number, value: number): number => Math.min(to, Math.max(from, value));

export const isDefined = <T>(value: T | undefined | null): value is T => value != null;

const toFixed = (value: number) => {
    if (value && typeof value === 'number') {
        //@ts-ignore
        const re = new RegExp(`^-?\\d+(?:.\\d{0,${2 || -1}})?`);
        const res = value.toString().match(re);
        return res ? Number(res[0]) : 0;
    }

    return value;
};

const difference = (roof: number, bottom: number) => {
    if (roof && bottom) {
        return Math.abs(toFixed(bottom - roof));
    }

    return '';
};

const useIsCorrelation = () => {
    const { activeGroupName } = useSelector((state: RootState) => state.tablets);
    const lists = useSelector((state: RootState) => state.lists);
    if (!activeGroupName) {
        return false;
    }
    const listData = lists.data?.find(({ key }) => key === activeGroupName);

    return (listData?.isCorrelation as boolean) ?? false;
};

const useFirstTableKey = () => {
    const isGroup = useSelector((state: RootState) => state.tablets.isGroup);
    const tablets = useSelector((state: RootState) => state.tablets.list);
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const activeGroupName = useSelector((state: RootState) => state.tablets.activeGroupName)!;
    const activeGroupIds = useSelector((state: RootState) => state.tablets.activeGroupIds);
    const activeTabletId = useSelector((state: RootState) => state.tablets.activeTabletId);

    const groupItems = useMemo(() => {
        if (isGroup && tablets?.[activeGroupName]) {
            const group = tablets[activeGroupName];
            const keys = getPropertiesFromObject(group);
            //@ts-ignore
            return excludeNonExistentIDs(activeGroupIds, keys).map(id => group[id]);
        }
        return [];
    }, [isGroup, tablets, activeGroupName, activeGroupIds]);

    if (groupItems.length) {
        return groupItems[0].key;
    }

    return tablets[activeTabletId]?.key;
};

export { toFixed, difference, useIsCorrelation, useFirstTableKey };
